import { SearchOutlined } from '@ant-design/icons'
import {
  Row,
  Table,
  Tabs,
  type TablePaginationConfig,
  Input
} from 'antd'
import { type FilterValue, type SorterResult } from 'antd/es/table/interface'
import { useState } from 'react'
import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { PageHeader } from '~/components/PageHeader'
import { genericApi } from '~/api'
import {
  type AppstreamSession,
  type SessionCourse,
  useAppstreamSessionsQuery
} from '~/hooks/useAppstreamSessionsQuery'
import { regionCodeToName } from '~/utils/constants'
import { formatDateTime } from '~/utils/helpers'

type ReportTab = 'users' | 'sessions'

interface UtilizationCourse {
  id: number
  name: string
  deleted_at: string | null
}

interface UtilizationParticipant {
  id: number
  first_name: string
  last_name: string
  email: string
  username: string
  course: UtilizationCourse | null
  appstream_sessions: AppstreamSession[]
  total_appstream_session_duration_in_seconds: number
  total_appstream_session_duration_in_minutes: number
  total_appstream_session_duration_in_hours: number
  total_appstream_session_duration_time_span: string
}

interface ParticipantSessionsResponse {
  data: UtilizationParticipant[]
  meta: {
    total: number
  }
  total_duration_in_hours: number
}

interface TableParams {
  pagination?: TablePaginationConfig
  sorter?: SorterResult<UtilizationParticipant>
  filters?: Record<string, FilterValue | null>
}

interface SessionSort {
  orderBy: string
  direction: 'asc' | 'desc'
}

const DEFAULT_SESSION_SORT: SessionSort = {
  orderBy: 'session_start_time',
  direction: 'desc'
}

const useParticipantSessionsQuery = (
  params: TableParams,
  search: string = ''
): UseQueryResult<ParticipantSessionsResponse> => {
  return useQuery({
    queryKey: ['get_participant_sessions', params, search],
    queryFn: async () => {
      const { data } = await genericApi.getParticipantSessions({
        page: params.pagination?.current,
        per_page: params.pagination?.pageSize,
        order_by: params.sorter?.columnKey,
        order_direction: params.sorter?.order,
        search
      })
      return data
    },
    keepPreviousData: true
  })
}

const renderEvent = (course: SessionCourse | UtilizationCourse | null | undefined) => {
  if (course == null) {
    return null
  }

  if (course.deleted_at != null) {
    return course.name
  }

  return <Link to={`/admin/courses/${course.id}`}>{course.name}</Link>
}

const UtilizationReport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ReportTab>('users')
  const [search, setSearch] = useState<string>('')
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 15
    }
  })
  const [sessionPagination, setSessionPagination] =
    useState<TablePaginationConfig>({
      current: 1,
      pageSize: 15
    })
  const [sessionSort, setSessionSort] =
    useState<SessionSort>(DEFAULT_SESSION_SORT)

  const { data, isFetching } = useParticipantSessionsQuery(tableParams, search)
  const {
    data: sessionsData,
    isFetching: isFetchingSessions
  } = useAppstreamSessionsQuery({
    include_course: 1,
    search,
    page: sessionPagination.current ?? 1,
    per_page: sessionPagination.pageSize ?? 15,
    order_by: sessionSort.orderBy,
    direction: sessionSort.direction
  }, activeTab === 'sessions')

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UtilizationParticipant> | Array<SorterResult<UtilizationParticipant>>
  ) => {
    setTableParams({
      pagination,
      filters,
      sorter: Array.isArray(sorter) ? sorter[0] : sorter
    })
  }

  const handleSessionTableChange = (
    pagination: TablePaginationConfig,
    _filters,
    sorter: SorterResult<AppstreamSession> | Array<SorterResult<AppstreamSession>>
  ) => {
    const selectedSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const nextSort = selectedSorter.order == null
      ? DEFAULT_SESSION_SORT
      : {
          orderBy: String(selectedSorter.columnKey),
          direction: selectedSorter.order === 'ascend' ? 'asc' as const : 'desc' as const
        }

    setSessionPagination(pagination)
    setSessionSort(nextSort)
  }

  const handleSearch = (value: string) => {
    setSearch(value.trim())
    setTableParams((current) => ({
      ...current,
      pagination: { ...current.pagination, current: 1 }
    }))
    setSessionPagination((current) => ({ ...current, current: 1 }))
  }

  const userTable = <Table<UtilizationParticipant>
    rowKey='id'
    size='small'
    tableLayout='auto'
    dataSource={data?.data}
    loading={isFetching}
    pagination={{
      ...tableParams.pagination,
      total: data?.meta.total
    }}
    onChange={handleTableChange}
    footer={() => (
      <Row justify='start'>
        <span>Total utilization: {data?.total_duration_in_hours} hours</span>
      </Row>
    )}
    expandable={{
      expandRowByClick: false,
      rowExpandable: item => item.appstream_sessions.length > 0,
      expandedRowRender: (record) => {
        return <Table<AppstreamSession>
          className='radup-utilization-items'
          size='small'
          tableLayout='auto'
          dataSource={record.appstream_sessions}
          pagination={false}
          rowKey='id'
          columns={[
            {
              key: 'location',
              title: 'Location',
              render: (_, session) => regionCodeToName(session.region)
            },
            {
              key: 'fleet_name',
              title: 'Fleet Name',
              dataIndex: 'fleet_name'
            },
            {
              key: 'session_start_time',
              title: 'Started at',
              render: (_, session) => formatDateTime(session.session_start_time)
            },
            {
              key: 'session_end_time',
              title: 'Ended at',
              render: (_, session) => formatDateTime(session.session_end_time)
            },
            {
              key: 'duration',
              title: 'Duration',
              dataIndex: 'session_duration_in_seconds_time_span'
            }
          ]}
        />
      }
    }}
    columns={[
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id'
      },
      {
        key: 'name',
        title: 'Name',
        render: (_, participant) => [
          participant.first_name,
          participant.last_name
        ].join(' ')
      },
      {
        key: 'email',
        title: 'Email',
        dataIndex: 'email'
      },
      {
        key: 'username',
        title: 'Username',
        dataIndex: 'username'
      },
      {
        key: 'course',
        title: 'Event',
        render: (_, participant) => renderEvent(participant.course)
      },
      {
        key: 'appstream_sessions_sum_session_duration_in_seconds',
        title: 'Seconds',
        dataIndex: 'total_appstream_session_duration_in_seconds',
        sorter: true
      },
      {
        key: 'appstream_sessions_sum_session_duration_in_seconds',
        title: 'Minutes',
        dataIndex: 'total_appstream_session_duration_in_minutes',
        sorter: true
      },
      {
        key: 'appstream_sessions_sum_session_duration_in_seconds',
        title: 'Hours',
        dataIndex: 'total_appstream_session_duration_in_hours',
        sorter: true
      },
      {
        key: 'appstream_sessions_sum_session_duration_in_seconds',
        title: 'Duration',
        dataIndex: 'total_appstream_session_duration_time_span',
        sorter: true
      }
    ]}
  />

  const sessionTable = <Table<AppstreamSession>
    rowKey='id'
    size='small'
    tableLayout='auto'
    dataSource={sessionsData?.data}
    loading={isFetchingSessions}
    pagination={{
      ...sessionPagination,
      total: sessionsData?.meta.total
    }}
    onChange={handleSessionTableChange}
    footer={() => (
      <Row justify='start'>
        <span>Total utilization: {sessionsData?.total_duration_in_hours} hours</span>
      </Row>
    )}
    columns={[
      {
        key: 'name',
        title: 'Name',
        render: (_, session) => session.participant == null
          ? ''
          : [session.participant.first_name, session.participant.last_name].join(' ')
      },
      {
        key: 'username',
        title: 'Username',
        dataIndex: 'user_id'
      },
      {
        key: 'email',
        title: 'Email',
        render: (_, session) => session.participant?.email ?? ''
      },
      {
        key: 'course',
        title: 'Event',
        render: (_, session) => renderEvent(session.course)
      },
      {
        key: 'region',
        title: 'Location',
        render: (_, session) => regionCodeToName(session.region)
      },
      {
        key: 'fleet_name',
        title: 'Fleet Name',
        dataIndex: 'fleet_name'
      },
      {
        key: 'session_start_time',
        title: 'Started at',
        render: (_, session) => formatDateTime(session.session_start_time),
        sorter: true,
        sortOrder: sessionSort.orderBy === 'session_start_time'
          ? sessionSort.direction === 'asc' ? 'ascend' : 'descend'
          : null
      },
      {
        key: 'session_end_time',
        title: 'Ended at',
        render: (_, session) => formatDateTime(session.session_end_time),
        sorter: true,
        sortOrder: sessionSort.orderBy === 'session_end_time'
          ? sessionSort.direction === 'asc' ? 'ascend' : 'descend'
          : null
      },
      {
        key: 'session_duration_in_seconds',
        title: 'Duration',
        dataIndex: 'session_duration_in_seconds_time_span',
        sorter: true,
        sortOrder: sessionSort.orderBy === 'session_duration_in_seconds'
          ? sessionSort.direction === 'asc' ? 'ascend' : 'descend'
          : null
      }
    ]}
  />

  return <>
    <PageHeader
      className='radup-page-header'
      title='Utilization Report'
      extra={[
        <Input.Search
          key='search'
          placeholder='Search'
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
        />
      ]}
    />

    <Tabs
      activeKey={activeTab}
      onChange={(key) => { setActiveTab(key as ReportTab) }}
      items={[
        {
          key: 'users',
          label: 'By user',
          children: userTable
        },
        {
          key: 'sessions',
          label: 'By session',
          children: sessionTable
        }
      ]}
    />
  </>
}

export default UtilizationReport
